// Basemap tile configuration — the raster basemap Leaflet renders under our data.
//
// LEAFLET DRAWS; THE PROVIDER SUPPLIES IMAGERY. react-leaflet renders wells, pads,
// cellar polygons, the Geoman lasso and all interaction; every dark square behind them
// is a PNG fetched from whichever provider is configured here. Nothing in SpatialMap's
// geometry, bounds-fitting or marker logic is provider-specific, which is why swapping
// is this file and nothing else.
//
// EVERY PROVIDER BELOW REQUIRES A KEY — verified 2026-08-27 by probing each endpoint
// unauthenticated: CARTO returns HTTP 200 with "API KEY REQUIRED" WATERMARKED INTO THE
// PNG (status codes and byte counts cannot see this — only rendering the tile can),
// MapTiler returns 403, Stadia returns 401. Choosing a provider does not avoid a signup;
// it only decides whose terms and retirement schedule you are on.
//
// CONFIGURE in apps/web/.env (not committed):
//     VITE_BASEMAP_PROVIDER=carto        # carto | maptiler | stadia
//     VITE_BASEMAP_API_KEY=your_key_here
//
// NOT A SECRET, deliberately — a basemap key ships in the client bundle by design (like
// VITE_GEONAMES_USERNAME) and is protected by DOMAIN RESTRICTION at the provider, not by
// hiding it. Restrict it to our domains in the provider console; do not route it through
// the backend or treat a leak as an incident.
//
// NO KEY => the map still renders (watermarked, or blank tiles on the keyed providers)
// rather than throwing. A visibly-wrong map is survivable mid-demo; a hard failure is a
// black void with markers floating on nothing.

export type BasemapProviderId = "carto" | "maptiler" | "stadia";

export interface BasemapProvider {
  id: BasemapProviderId;
  label: string;
  /** `{key}` is substituted; Leaflet substitutes {s}/{z}/{x}/{y}/{r} itself. */
  urlTemplate: string;
  attribution: string;
  /** Deepest zoom with REAL tiles. Leaflet upscales beyond it (see MAX_ZOOM in
   *  SpatialMap — cellar-scale authoring needs z22 from z20 imagery). */
  maxNativeZoom: number;
  signupUrl: string;
  /** Why you might pick, or avoid, this one. */
  note: string;
}

export const BASEMAP_PROVIDERS: Record<BasemapProviderId, BasemapProvider> = {
  carto: {
    id: "carto",
    label: "CARTO Dark Matter",
    urlTemplate:
      "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key={key}",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
    maxNativeZoom: 20,
    signupUrl: "https://carto.com/basemaps/apikey",
    // CARTO states raster basemaps are BEING RETIRED in favour of vector and "may stop
    // receiving data updates". Fine today; it is the reason MAP-01 exists.
    note: "current default; raster is deprecated by CARTO",
  },
  maptiler: {
    id: "maptiler",
    label: "MapTiler Dataviz Dark",
    urlTemplate:
      "https://api.maptiler.com/maps/dataviz-dark/{z}/{x}/{y}{r}.png?key={key}",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>',
    maxNativeZoom: 22,
    signupUrl: "https://cloud.maptiler.com/account/keys/",
    note: "raster not deprecated; native tiles to z22 (deeper than CARTO's z20)",
  },
  stadia: {
    id: "stadia",
    label: "Stadia Alidade Smooth Dark",
    urlTemplate:
      "https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key={key}",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://stadiamaps.com/">Stadia Maps</a>',
    maxNativeZoom: 20,
    signupUrl: "https://client.stadiamaps.com/",
    note: "raster not deprecated; note the key param is api_key, not key",
  },
};

/** PURE resolver — the whole of the logic, with env read by the caller.
 *
 *  Kept pure because `import.meta.env.VITE_*` is STATICALLY REPLACED by Vite at
 *  transform time when the var exists in `.env`, which defeats `vi.stubEnv`. Tests
 *  that drove the module-level constants therefore passed on a machine with no
 *  `.env` and FAILED the moment a real key was configured — caught by the
 *  pre-commit hook, 2026-08-27. Testing this function instead makes the behaviour
 *  independent of whoever's `.env` is on disk. */
export function resolveBasemap(
  providerId: string | undefined,
  apiKey: string | undefined,
): {
  provider: BasemapProvider;
  tileUrl: string;
  keyMissing: boolean;
} {
  // An unrecognised provider falls back to the default rather than throwing: a typo
  // in a .env should not black out every map in the app.
  const provider =
    BASEMAP_PROVIDERS[providerId?.trim() as BasemapProviderId] ??
    BASEMAP_PROVIDERS.carto;
  const key = (apiKey ?? "").trim();
  const keyMissing = key === "";
  // No key => DROP the whole query rather than send it empty. An empty key reads as
  // present-but-invalid (403), a different and more confusing failure than absent.
  const tileUrl = keyMissing
    ? provider.urlTemplate.replace(/[?&](api_)?key=\{key\}/, "")
    : provider.urlTemplate.replace("{key}", encodeURIComponent(key));
  return { provider, tileUrl, keyMissing };
}

// VITE_CARTO_API_KEY is honoured for back-compat with the 2026-08-27 first fix, so an
// existing .env keeps working after this generalisation.
const resolved = resolveBasemap(
  import.meta.env.VITE_BASEMAP_PROVIDER as string | undefined,
  (import.meta.env.VITE_BASEMAP_API_KEY as string | undefined) ??
    (import.meta.env.VITE_CARTO_API_KEY as string | undefined),
);

export const BASEMAP_PROVIDER = resolved.provider;

/** True when no key is configured — tiles will be watermarked or refused. */
export const BASEMAP_KEY_MISSING = resolved.keyMissing;

/** Tile URL with the key substituted (see resolveBasemap). */
export const BASEMAP_TILE_URL = resolved.tileUrl;

/** Required by every provider's terms AND by OSM's — never drop it on a swap. */
export const BASEMAP_ATTRIBUTION = BASEMAP_PROVIDER.attribution;

export const BASEMAP_MAX_NATIVE_ZOOM = BASEMAP_PROVIDER.maxNativeZoom;
